import { createRouter, createWebHistory } from 'vue-router';
import { installStaleChunkReload } from './staleChunk.js';
import { SECTIONS } from './sections.js';

// Dispatch is eagerly loaded since it's the default route
import Dispatch from '../pages/Dispatch/Dispatch.vue';

// All other routes use lazy loading for code splitting
const Users = () => import('../pages/Users/UsersList.vue');
const EditUser = () => import('../pages/Users/EditUser.vue');

const Trucks = () => import('../pages/Trucks/TrucksList.vue');
const TrucksCreate = () => import('../pages/Trucks/TrucksCreate.vue');
const TrucksEdit = () => import('../pages/Trucks/TrucksEdit.vue');
const TrucksView = () => import('../pages/Trucks/TrucksView.vue');

const Locations = () => import('../pages/Locations/LocationsList.vue');
const LocationsCreate = () => import('../pages/Locations/LocationsCreate.vue');
const LocationsEdit = () => import('../pages/Locations/LocationsEdit.vue');

const Drivers = () => import('../pages/Drivers/DriversList.vue');
const DriversCreate = () => import('../pages/Drivers/DriversCreate.vue');
const DriversEdit = () => import('../pages/Drivers/DriversEdit.vue');
const SubhaulersEdit = () => import('../pages/Subhaulers/SubhaulersEdit.vue');
// Spec 26: the IFTA miles report, the first page under /reports.
const IftaReport = () => import('../pages/Reports/IftaReport.vue');
// Carrier workflows, Phase 1: settlements (lazy, like the IFTA report).
const Settlements = () => import('../pages/Settlements/Settlements.vue');
const SettlementDetail = () => import('../pages/Settlements/SettlementDetail.vue');
// Spec 41: expense capture (admins and accountants).
const Expenses = () => import('../pages/Expenses/Expenses.vue');

const LoadsCreate = () => import('../pages/Loads/LoadsCreate.vue');
// Carrier workflows Phase 2: the rate-confirmation import.
const ImportsList = () => import('../pages/Imports/ImportsList.vue');
const ImportReview = () => import('../pages/Imports/ImportReview.vue');
const LoadsView = () => import('../pages/Loads/LoadsView.vue');
const LoadsUpdate = () => import('../pages/Loads/LoadsUpdate.vue');

const Customers = () => import('../pages/Customers/CustomersList.vue');
const CustomersCreate = () => import('../pages/Customers/CustomersCreate.vue');
const CustomersEdit = () => import('../pages/Customers/CustomersEdit.vue');
const CustomerLocations = () => import('../components/pages/Customers/customerLocations.vue');
// Spec 16 M2: customerLanes.vue is deleted; rate agreements are the shape
// that replaced the lanes concept, and this tab is its surface.
const CustomerRateAgreements = () =>
  import('../components/pages/Customers/customerRateAgreements.vue');
const CustomerSettings = () => import('../components/pages/Customers/customerSettings.vue');
const CustomerNotes = () => import('../components/pages/Customers/customerNotes.vue');

// The live map. Lazy like every other page here, and that matters more than
// usual for this one: it is the only route that pulls MapLibre in, and an eager
// import would put a map engine in the chunk every dispatcher loads to reach
// the board.
const Tracking = () => import('../pages/Tracking/Tracking.vue');

const Billing = () => import('../pages/Billing/Billing.vue');
const Integrations = () => import('../pages/Settings/Integrations.vue');
const Devices = () => import('../pages/Settings/Devices.vue');
const Preferences = () => import('../pages/Settings/Preferences.vue');
// Spec 42: the caller's own daily exceptions digest.
const DailyDigest = () => import('../pages/Settings/DailyDigest.vue');
const Documents = () => import('../pages/Documents/Documents.vue');
// Spec 15 4.3 RESOLVED: the signature-field placement designer, a full-page
// route rather than a dialog (LwDialog caps at 480px; a placement surface
// needs the page). Reached from the Templates tab's Send action.
const SendDesigner = () => import('../pages/Documents/SendDesigner.vue');
// Spec 19 WP3: the TEMPLATE designer, the second full-page route onto the
// same placement canvas. Reached from the Templates tab's Design action,
// beside Send. Where SendDesigner places fields for one envelope's signers,
// this one places them for ROLES and saves the layout on the template row,
// so every later send reuses it instead of re-placing it.
const TemplateDesigner = () => import('../pages/Documents/TemplateDesigner.vue');
// Spec 19 WP4: the ROLE-MODE send form. The Templates tab's Send action
// routes here when the template HAS a saved field set and to SendDesigner
// when it does not (spec 19 5.4: a paper-only template keeps working exactly
// as it does today). Two routes rather than one page with a mode flag,
// because the two post different request shapes to POST /api/documents and
// the API dispatches on exactly that difference.
const SendFromTemplate = () => import('../pages/Documents/SendFromTemplate.vue');
const Team = () => import('../pages/Settings/Team.vue');
// Spec 21 work package 4: what the organization pays LADEWAY (plan, billing
// email, card or bank account through Stripe). Under Settings and named
// "Ladeway billing", apart from /billing, the freight page where a carrier
// bills its own shippers (spec 12's vocabulary rule).
const LadewayBilling = () => import('../pages/Settings/LadewayBilling.vue');
// Spec 47: the tenant's factoring companies (admins and accountants).
const FactoringSettings = () => import('../pages/Settings/Factoring.vue');
const CreateInvoice = () => import('../pages/Billing/invoice/CreateInvoice.vue');

// The platform-subscription page (spec 12 Part 4): what a suspended admin
// sees, and where the billing banner links. Deliberately NOT `/billing`,
// which is the freight billing page (spec 12's vocabulary rule, :15-21) -
// this is "your Ladeway subscription," not a freight invoice.
const Subscription = () => import('../pages/Subscription/Subscription.vue');

// The design refresh phase 1 story fixture: every Lw* component, every state,
// on one route (refresh_plan.md section 3). It is deliberately not in the
// sidebar. Phase 1 ships a component library and no migrated surface, so this
// is the only place the work is visible until phase 2 starts, and a nav entry
// would make an engineering fixture look like a product page.
const LwGallery = () => import('../pages/Design/LwGallery.vue');
// Carrier workflows, Phase 7A (decision D-08): the scheduling board.
const Scheduling = () => import('../pages/Scheduling/Scheduling.vue');
// Spec 44: the run builder. The runs LIST is a tab of the dispatch board
// (spec 49), so it has no route of its own any more; /runs redirects there.
const RunBuilder = () => import('../pages/Runs/RunBuilder.vue');
// Spec 45: profit per load, run, truck and customer.
const Profit = () => import('../pages/Reports/Profit.vue');
// Spec 39: the office's message inbox (load and driver threads).
const MessagesInbox = () => import('../pages/Messages/MessagesInbox.vue');
// Spec 37: credentials expired or expiring. One page, rendered twice (spec
// 49): the Team section shows the driver and subhauler half, the Equipment
// section the truck, trailer and company half.
const FleetExpiring = () => import('../pages/Fleet/FleetExpiring.vue');
// Spec 38: maintenance, work orders, out of service, inspection reports.
const FleetMaintenance = () => import('../pages/Fleet/FleetMaintenance.vue');
const TruckMaintenance = () => import('../pages/Fleet/TruckMaintenance.vue');
// Spec 27 (IFTA phase 2): fuel purchases, the card import and the review
// queue. Lazy like every other page; the review page is the third route that
// pulls MapLibre in, and only it does.
const FuelPurchases = () => import('../pages/Fuel/FuelPurchases.vue');
const FuelImport = () => import('../pages/Fuel/FuelImport.vue');
const FuelReview = () => import('../pages/Fuel/FuelReview.vue');
// Spec 53: required training. The gate page (in the bare layout, for a gated
// user), the offered list, and the practice fleet's own list; one module's
// video and Practice.
const TrainingHome = () => import('../pages/Training/TrainingHome.vue');
const TrainingModule = () => import('../pages/Training/TrainingModule.vue');
// Spec 53 6.1: a practice fleet's books send Connect here, not to a provider.
const PracticeConsent = () => import('../pages/Training/PracticeConsent.vue');

// Spec 49: the shell every section's pages render inside (one header, the
// row of section tabs, a router-view). Eager: it is the parent of half the
// routes in the table and weighs nothing.
import SectionLayout from '../components/sections/SectionLayout.vue';

// Spec 49: the page behind each section tab, by route name. sections.js
// describes the tabs without importing a page so the rail can read it for
// free; this is where the pages are attached. A tab here with no component
// is a mistake the test suite reports, not something the router papers over.
const SECTION_PAGES = {
  Customers,
  Locations,
  Drivers,
  TeamExpiring: FleetExpiring,
  Scheduling,
  Settlements,
  Team,
  Trucks,
  FleetMaintenance,
  FleetExpiring,
  IftaReport,
  FuelPurchases,
  Expenses,
  Profit,
  Integrations,
  Devices,
  Preferences,
  DailyDigest,
  LadewayBilling,
  FactoringSettings,
};

// Per-tab props. The Expiring page takes the holders it should list.
const SECTION_PROPS = {
  TeamExpiring: { holders: ['driver', 'subhauler'] },
  FleetExpiring: { holders: ['truck', 'trailer', 'org'] },
};

function sectionRoute(section) {
  return {
    path: section.path,
    component: SectionLayout,
    props: { sectionKey: section.key },
    meta: { title: section.title, section: section.key },
    children: section.tabs.map(tab => ({
      path: tab.path,
      name: tab.name,
      component: SECTION_PAGES[tab.name],
      props: SECTION_PROPS[tab.name],
      meta: { title: tab.title, subtitle: tab.subtitle || null, section: section.key },
    })),
  };
}

// Spec 49 section 4.3: every path that existed before the sections keeps
// working. The server's emails and notices hardcode several of them
// (/fleet/expiring, /charges?..., /settlements?...), bookmarks hold the
// rest, and none of those can be recalled. Each redirect carries the query
// and the hash through; `target` is a location or a function of the old
// route (for the ones with params).
function moved(path, target) {
  return {
    path,
    redirect: to => {
      const location = typeof target === 'function' ? target(to) : target;
      return { ...location, query: { ...location.query, ...to.query }, hash: to.hash };
    },
  };
}

const router = createRouter({
  // mode: 'history' in vue-router 3 terms: real URLs, no hash.
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: '/dispatch' },
    { path: '/dispatch', component: Dispatch, name: 'Dispatch', meta: { title: 'Dispatch' } },
    // Spec 44's run builder, under the board it is a view of (spec 49).
    {
      path: '/dispatch/runs/:id',
      component: RunBuilder,
      name: 'RunView',
      meta: { title: 'Run' },
    },

    // Was a separate Vue 3 app served at /tracking/ while this one was Vue 2.
    // Paths match non-strictly, so the trailing slash every old bookmark and
    // every old sidebar link carries still resolves here.
    { path: '/tracking', component: Tracking, name: 'Tracking', meta: { title: 'Live Map' } },

    { path: '/billing', component: Billing, name: 'Billing', meta: { title: 'Billing' } },
    { path: '/billing/:loadid', component: CreateInvoice, name: 'CreateInvoice' },

    { path: '/documents', component: Documents, name: 'Documents', meta: { title: 'Documents' } },
    {
      path: '/documents/templates/:id/send',
      component: SendDesigner,
      name: 'DocumentSend',
      meta: { title: 'Send for signature' },
    },
    {
      path: '/documents/templates/:id/send-from-template',
      component: SendFromTemplate,
      name: 'DocumentSendFromTemplate',
      meta: { title: 'Send for signature' },
    },
    {
      path: '/documents/templates/:id/design',
      component: TemplateDesigner,
      name: 'DocumentTemplateDesign',
      meta: { title: 'Template designer' },
    },

    // Spec 53 3.3: where the router guard (trainingGuard.js) sends a gated
    // user, and the only two routes it lets them have.
    { path: '/training', component: TrainingHome, name: 'Training', meta: { title: 'Training' } },
    {
      path: '/training/:id',
      component: TrainingModule,
      name: 'TrainingModule',
      meta: { title: 'Training' },
    },
    // Spec 53 6.1: the practice sign-in a practice fleet's Connect opens
    // (Integrations), standing in for the accounting system's consent page.
    {
      path: '/training/consent/:provider',
      component: PracticeConsent,
      name: 'PracticeConsent',
      meta: { title: 'Practice sign-in' },
    },

    { path: '/users', component: Users, name: 'Users', meta: { title: 'Users' } },
    { path: '/users/:id', component: EditUser, name: 'EditUser' },

    // ---- The sections (spec 49) ------------------------------------------
    // /customers, /team, /equipment, /reports, /settings: a parent record
    // rendering SectionLayout, one child per tab. Everything that is a page
    // UNDER a tab (a driver, a statement, a truck, the fuel import) is its
    // own top-level record below, with a path inside the section's but no
    // section header or tab row of its own.
    ...SECTIONS.map(sectionRoute),

    // Customers section: create and detail pages, and the location pages.
    // '/customers/locations/...' is declared with literal segments, so a
    // location's id is never read as a customer's.
    {
      path: '/customers/create',
      component: CustomersCreate,
      name: 'CustomersCreate',
      meta: { title: 'Create Customer' },
    },
    {
      path: '/customers/locations/create',
      component: LocationsCreate,
      name: 'LocationsCreate',
      meta: { title: 'Create Location' },
    },
    {
      path: '/customers/locations/:id',
      component: LocationsEdit,
      name: 'LocationsEdit',
      meta: { title: 'Edit Location' },
    },
    {
      path: '/customers/:id',
      component: CustomersEdit,
      name: 'CustomersEdit',
      meta: { title: 'Edit Customer' },
      // A function, not the '/customers/:id/settings' string this was under
      // vue-router 3: v4 does not interpolate params into string redirects.
      redirect: to => ({ name: 'CustomerSettings', params: { id: to.params.id } }),
      children: [
        {
          path: 'locations',
          component: CustomerLocations,
          name: 'CustomerLocations',
        },
        // SEMANTIC CHANGE (spec 16 M2): 'lanes' is gone as a child path;
        // the tab it rendered is the Rate agreements tab now. A bookmarked
        // /customers/:id/lanes lands on the catch-all redirect below, like
        // any other retired path.
        {
          path: 'rate-agreements',
          component: CustomerRateAgreements,
          name: 'CustomerRateAgreements',
        },
        {
          path: 'notes',
          component: CustomerNotes,
          name: 'CustomerNotes',
        },
        {
          path: 'settings',
          component: CustomerSettings,
          name: 'CustomerSettings',
        },
      ],
    },

    // Team section: driver, subhauler and statement pages.
    {
      path: '/team/drivers/create',
      component: DriversCreate,
      name: 'DriversCreate',
      meta: { title: 'Create Driver' },
    },
    // Mark 2026-10-10: the pay report is a tab of Settlements now (it pays
    // the same drivers and subhaulers). The path and the name stay as a
    // redirect, so bookmarks and `{ name: 'SubhaulerReport' }` links land on
    // that tab. A literal segment, so "report" is never read as a driver id.
    {
      path: '/team/drivers/report',
      name: 'SubhaulerReport',
      redirect: () => ({ name: 'Settlements', query: { tab: 'pay-report' } }),
    },
    // The bell's `driver` subject links to a route named DriverEdit; the
    // driver page is DriversEdit. A named redirect keeps the bell's name
    // true without a second copy of the page. Since the driver page grew a
    // Schedule tab (Mark, 2026-10-13) the path means what it says and lands
    // there; a query the link carried rides along.
    {
      path: '/team/drivers/:id/schedule',
      name: 'DriverEdit',
      redirect: to => ({
        name: 'DriversEdit',
        params: { id: to.params.id },
        query: { ...to.query, tab: 'schedule' },
        hash: to.hash,
      }),
    },
    {
      path: '/team/drivers/:id',
      component: DriversEdit,
      name: 'DriversEdit',
      meta: { title: 'Driver' },
    },
    // Spec 30 section 10: drivers and subhaulers are one "Drivers" area. The
    // subhauler list IS the Drivers list filtered to subhaulers, so this
    // redirects there (the name stays, so a `{ name: 'Subhaulers' }` link
    // still lands somewhere true), and creating one is the Drivers create
    // page with the Subhauler kind chosen.
    {
      path: '/team/subhaulers',
      name: 'Subhaulers',
      redirect: () => ({ name: 'Drivers', query: { kind: 'subhauler' } }),
    },
    {
      path: '/team/subhaulers/create',
      component: DriversCreate,
      name: 'SubhaulersCreate',
      props: { defaultKind: 'subhauler' },
      meta: { title: 'Create Subhauler' },
    },
    {
      path: '/team/subhaulers/:id/edit',
      component: SubhaulersEdit,
      name: 'SubhaulersEdit',
      meta: { title: 'Subhauler' },
    },
    // The detail page's short form. A function: v4 does not interpolate
    // params into a string redirect (the /customers/:id note above).
    {
      path: '/team/subhaulers/:id',
      redirect: to => ({ name: 'SubhaulersEdit', params: { id: to.params.id } }),
    },
    {
      path: '/team/settlements/:id',
      component: SettlementDetail,
      name: 'SettlementDetail',
      meta: { title: 'Statement' },
    },

    // Equipment section: the unit pages.
    {
      path: '/equipment/create',
      component: TrucksCreate,
      name: 'TrucksCreate',
      meta: { title: 'Create Truck' },
    },
    // SEMANTIC CHANGE, 2026-09-27 (Mark: "be able to click into individual
    // equipment to further inspect read-only views of equipment (but have an
    // edit button that opens up edit panes)"). The detail view owns the bare
    // id and the form sits a segment down, which also means every existing
    // link to `{ name: 'TrucksEdit' }` still lands on the form.
    {
      path: '/equipment/:id/edit',
      component: TrucksEdit,
      name: 'TrucksEdit',
      meta: { title: 'Edit Truck' },
    },
    // Spec 38: one unit's maintenance (PM, work orders, out of service,
    // inspection reports). The inspection-report notice links here.
    {
      path: '/equipment/:id/maintenance',
      component: TruckMaintenance,
      name: 'TruckMaintenance',
      meta: { title: 'Maintenance' },
    },
    {
      path: '/equipment/:id',
      component: TrucksView,
      name: 'TrucksView',
      meta: { title: 'Equipment' },
    },

    // Reports section: the fuel card import and the review queue, two
    // literal children of the Fuel tab's path.
    {
      path: '/reports/fuel/import',
      component: FuelImport,
      name: 'FuelImport',
      meta: { title: 'Import fuel card file' },
    },
    {
      path: '/reports/fuel/review',
      component: FuelReview,
      name: 'FuelReview',
      meta: { title: 'Fuel review' },
    },

    {
      path: '/loads/create',
      component: LoadsCreate,
      name: 'LoadsCreate',
      meta: { title: 'Create Load' },
    },
    { path: '/loads/:id', component: LoadsView, name: 'LoadsView', meta: { title: 'View Load' } },
    {
      path: '/loads/:id/update',
      component: LoadsUpdate,
      name: 'LoadsUpdate',
      meta: { title: 'Edit Load' },
    },

    // No nav entry (nav.vue's own comment: entries are not role-gated, and
    // this would be noise for every dispatcher in every healthy org). Reached
    // only via the billing banner's "View subscription" link and the 402
    // interceptor redirect in app.vue.
    {
      path: '/subscription',
      component: Subscription,
      name: 'Subscription',
      meta: { title: 'Subscription' },
    },

    {
      path: '/design/components',
      component: LwGallery,
      name: 'LwGallery',
      meta: { title: 'Component library' },
    },

    // Carrier workflows Phase 2: Import load. The list and the review page.
    {
      path: '/imports/rate-confirmations',
      component: ImportsList,
      name: 'Imports',
      meta: { title: 'Import load' },
    },
    {
      path: '/imports/rate-confirmations/:id',
      component: ImportReview,
      name: 'ImportLoad',
      meta: { title: 'Import load' },
    },
    // Spec 39: the message inbox.
    {
      path: '/messages',
      component: MessagesInbox,
      name: 'MessagesInbox',
      meta: { title: 'Messages' },
    },

    // ---- Paths that moved (spec 49 section 4.3) ---------------------------
    // Two of these keep a NAME, because links address them by it: `Runs` and
    // `ChargesQueue` were pages and are tabs now, and a `{ name: 'Runs' }`
    // link must still land on the runs.
    {
      path: '/runs',
      name: 'Runs',
      redirect: to => ({ name: 'Dispatch', query: { ...to.query, tab: 'runs' }, hash: to.hash }),
    },
    moved('/runs/:id', to => ({ name: 'RunView', params: { id: to.params.id } })),
    // The charges queue is Billing's Charges tab; its own status tabs became
    // a `status` filter there, so an old `?tab=proposed` becomes
    // `?tab=charges&status=proposed`.
    {
      path: '/charges',
      name: 'ChargesQueue',
      redirect: to => {
        const { tab, ...rest } = to.query;
        const query = { ...rest, tab: 'charges' };
        if (tab && tab !== 'all') query.status = tab;
        return { name: 'Billing', query, hash: to.hash };
      },
    },
    moved('/locations', { name: 'Locations' }),
    moved('/locations/create', { name: 'LocationsCreate' }),
    moved('/locations/:id', to => ({ name: 'LocationsEdit', params: { id: to.params.id } })),
    moved('/drivers', { name: 'Drivers' }),
    moved('/drivers/create', { name: 'DriversCreate' }),
    moved('/drivers/report', { name: 'SubhaulerReport' }),
    moved('/drivers/:id/schedule', to => ({ name: 'DriverEdit', params: { id: to.params.id } })),
    moved('/drivers/:id', to => ({ name: 'DriversEdit', params: { id: to.params.id } })),
    moved('/subhaulers', { name: 'Subhaulers' }),
    moved('/subhaulers/create', { name: 'SubhaulersCreate' }),
    moved('/subhaulers/:id/edit', to => ({ name: 'SubhaulersEdit', params: { id: to.params.id } })),
    moved('/subhaulers/:id', to => ({ name: 'SubhaulersEdit', params: { id: to.params.id } })),
    moved('/scheduling', { name: 'Scheduling' }),
    moved('/settlements', { name: 'Settlements' }),
    moved('/settlements/:id', to => ({ name: 'SettlementDetail', params: { id: to.params.id } })),
    moved('/settings/team', { name: 'Team' }),
    moved('/trucks', { name: 'Trucks' }),
    moved('/trucks/create', { name: 'TrucksCreate' }),
    // Three routes where one list with a filter belongs (feedback triage
    // item 21): the Active and Inactive pages are the list, filtered.
    moved('/trucks/active', { name: 'Trucks', query: { status: 'active' } }),
    moved('/trucks/inactive', { name: 'Trucks', query: { status: 'inactive' } }),
    moved('/trucks/:id/edit', to => ({ name: 'TrucksEdit', params: { id: to.params.id } })),
    moved('/trucks/:id/maintenance', to => ({
      name: 'TruckMaintenance',
      params: { id: to.params.id },
    })),
    moved('/trucks/:id', to => ({ name: 'TrucksView', params: { id: to.params.id } })),
    moved('/fleet/expiring', { name: 'FleetExpiring' }),
    moved('/fleet/maintenance', { name: 'FleetMaintenance' }),
    moved('/fuel', { name: 'FuelPurchases' }),
    moved('/fuel/import', { name: 'FuelImport' }),
    moved('/fuel/review', { name: 'FuelReview' }),
    moved('/expenses', { name: 'Expenses' }),

    // vue-router 4 spells the catch-all as a param with a custom regexp; a
    // bare '*' is rejected at router creation. It must stay last.
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

router.beforeEach(to => {
  let newTitle = 'Ladeway';

  if (to.meta && to.meta.title) {
    newTitle = `${to.meta.title} | Ladeway`;
  }

  document.title = newTitle;
});

// A tab open across a deploy: reload onto the page asked for (staleChunk.js).
installStaleChunkReload(router);

export default router;
